import SelectionState from "state/selections/SelectionState";
import { selectSideBySideValue } from "state/selections/selector";
import { selectDefinitions } from "./selectors";
import { useDispatch, useSelector } from "react-redux";
import React, { useMemo, useState } from "react";
import { toBetaRowField, toColId, toFaRowField, toRowField } from "./util";
import { deleteColumn } from "state/grid/actions";
import { selectBaseGroupingParams } from "state/selections/selector";
import { QueryObserverOptions, useQueries } from "react-query";
import PivotFilterDefinition from "./PivotFilterDefinition";
import SummaryType from "state/selections/Constants/SummaryType";
import { isDefined } from "@ls/js-utils";
import useDeepCompareEffect from "use-deep-compare-effect";
import { useQueryEnabled } from "state/selections/hooks";
import PivotDefinition from "./PivotDefinition";
import { ColDef } from "module/grid";
import { resolveIn2ServiceUrl } from "@ls/in2-url-mapper";
import { exchange } from "@ls/js-fetch";

export const useColumnDefinitions = () => {
  const pivotDefinitions = useSelector<any, PivotDefinition[]>(
    selectDefinitions(SelectionState.COMMITTED),
  );
  const sideBySide = useSelector(
    selectSideBySideValue(SelectionState.COMMITTED),
  );
  const dispatch = useDispatch();
  const [baseQueries, sbsQueries] = useDataQueries();
  return React.useMemo(() => {
    const colIdSet = new Set(); //to ensure only one colId is set for bmk and accounts since iterating over every grouping node will give colId for both
    return pivotDefinitions.reduce((columnDefs: ColDef[], definition, idx) => {
      const baseQuery: any = baseQueries[idx],
        sbsQuery = sbsQueries[idx];
      const {
        key,
        levels,
        analytics,
        betaDefinitions,
        faDefinitions,
        summary,
        scaleType,
      } = definition;
      if (baseQuery?.isFetching) {
        columnDefs.push({
          headerName: `${levels || "N/A"} ${analytics} ${summary} ${scaleType}`,
          colId: key,
          X_onDelete: () => dispatch(deleteColumn(definition)),
          loading: true,
        });
      }

      const groupingNodes = baseQuery?.data?.root?.groupingNodes ?? [];

      groupingNodes.forEach((groupingNode) => {
        const { bucket = "N/A" } = groupingNode;
        const colId = toColId(definition, bucket);

        if (!colIdSet.has(colId)) {
          colIdSet.add(colId);
          const pivotColumnDef: ColDef = {
            headerName: `${bucket || "N/A"}, ${analytics} ${scaleType}`,
            headerTooltip: levels,
            field: toRowField(bucket, definition.key),
            X_onDelete: () => dispatch(deleteColumn(definition)),
            colId,
          };
          columnDefs.push(pivotColumnDef);

          //handle side by side column
          if (sideBySide) {
            const pivotColumnDefSbs = { ...pivotColumnDef };
            pivotColumnDefSbs.colId += " - scenario";
            pivotColumnDefSbs.field += " - scenario";
            pivotColumnDefSbs.headerName += " - scenario";
            pivotColumnDefSbs.loading = sbsQuery?.isFetching;
            pivotColumnDefSbs.X_sbs = true; //useful for differentiating 'sbs' columns from their original columns
            pivotColumnDefSbs.X_originalColId = colId;
            pivotColumnDefSbs.X_backgroundColor = "#fffb95";
            columnDefs.push(pivotColumnDefSbs);
          }

          if (betaDefinitions.length > 0) {
            betaDefinitions.forEach((betaDef) => {
              const { betaId, covarianceType } = betaDef;
              const pivotBetaColumnDef: ColDef = {
                headerName: `${pivotColumnDef.headerName}\n(Beta ${betaId}-${covarianceType})`,
                headerTooltip: levels,
                field: toBetaRowField(bucket, key, betaDef.key),
                colId: `${colId}_${betaDef.key}`,
              };
              columnDefs.push(pivotBetaColumnDef);
              if (sideBySide) {
                const pivotBetaColumnDefSbs = { ...pivotBetaColumnDef };
                pivotBetaColumnDefSbs.colId += " - scenario";
                pivotBetaColumnDefSbs.field += " - scenario";
                pivotBetaColumnDefSbs.headerName += " - scenario";
                pivotBetaColumnDefSbs.loading = sbsQuery?.isFetching;
                pivotBetaColumnDefSbs.X_sbs = true; //useful for differentiating 'sbs' columns from their original columns
                pivotBetaColumnDefSbs.X_originalColId =
                  pivotBetaColumnDef.colId;
                pivotBetaColumnDefSbs.X_backgroundColor = "#fffb95";
                columnDefs.push(pivotBetaColumnDefSbs);
              }
            });
          }
          if (faDefinitions.length > 0) {
            faDefinitions.forEach((faDef) => {
              const { factribFactor, factribPeriod } = faDef;
              const pivotFaColumnDef: ColDef = {
                headerName: `${pivotColumnDef.headerName}\n(FAA ${factribFactor} - ${factribPeriod})`,
                headerTooltip: levels,
                field: toFaRowField(bucket, key, faDef.key),
                colId: `${colId}_${faDef.key}`,
              };
              columnDefs.push(pivotFaColumnDef);
              if (sideBySide) {
                const pivotFaColumnDefSbs = { ...pivotFaColumnDef };
                pivotFaColumnDefSbs.colId += " - scenario";
                pivotFaColumnDefSbs.field += " - scenario";
                pivotFaColumnDefSbs.headerName += " - scenario";
                pivotFaColumnDefSbs.loading = sbsQuery?.isFetching;
                pivotFaColumnDefSbs.X_sbs = true; //useful for differentiating 'sbs' columns from their original columns
                pivotFaColumnDefSbs.X_originalColId = pivotFaColumnDef.colId;
                pivotFaColumnDefSbs.X_backgroundColor = "#fffb95";
                columnDefs.push(pivotFaColumnDefSbs);
              }
            });
          }
        }
      });
      return columnDefs;
    }, []);
  }, [baseQueries, dispatch, pivotDefinitions, sbsQueries, sideBySide]);
};

export const useRowsMap = () => {
  const [baseQueries, sbsQueries] = useDataQueries();
  const definitions = useSelector<any, PivotDefinition[]>(
    selectDefinitions(SelectionState.COMMITTED),
  );

  return useMemo(() => {
    const entityRowsMap = {};
    definitions.forEach((pivotDefinition, idx) => {
      const { key, analytics, summary, betaDefinitions, faDefinitions } =
        pivotDefinition;
      const baseQuery = baseQueries[idx],
        sbsQuery = sbsQueries[idx];
      const groupingResponse: any = baseQuery?.data;
      const groupingResponseSbs: any = sbsQuery?.data;
      const groupingNodes = groupingResponse?.root?.groupingNodes ?? [];
      const groupingNodesSbs = groupingResponseSbs?.root?.groupingNodes ?? [];

      const benchmarkEntityIdSet = new Set(
        [
          ...(groupingResponse?.attributes ?? []),
          groupingResponseSbs?.attributes ?? [],
        ]
          .filter(
            ({ benchmarkId }) => benchmarkId !== undefined && benchmarkId !== 0,
          )
          .map(({ benchmarkId }) => benchmarkId) ?? [],
      );

      const processEntitySummary = (bucket, entitySummary, scenario) => {
        const {
          values,
          percentiles,
          betaSummaries,
          slaSummaries,
          entityResultMetaData,
        } = entitySummary;
        const { entityId, relative, benchId } = entityResultMetaData;
        let rowField = toRowField(bucket, key);
        if (scenario) {
          rowField += " - scenario";
        }

        if (!entityRowsMap[entityId]) {
          entityRowsMap[entityId] = {};
        }

        let add2RowMap = false;
        if (benchmarkEntityIdSet.has(entityId)) {
          // if bmk, treat it differently
          if (!isDefined(entityRowsMap[entityId]?.[rowField]) && relative) {
            //if it's relative, make sure it doesn't already have a value since we want to honor absolutes for bmks first
            add2RowMap = true;
          } else if (!relative && isDefined(values?.[analytics])) {
            //honor absolute values first for bmks, only if it exists
            add2RowMap = true;
          }
        } else if (benchId === 0 && !relative) {
          //doesn't have benchmark, only grab abs
          add2RowMap = true;
        } else if (
          (summary === SummaryType.RELATIVE && relative) ||
          (summary === SummaryType.ABSOLUTE && !relative)
        ) {
          //simply make sure summary values match
          add2RowMap = true;
        }

        const percentileScaleTypes = new Set(["25", "75", "Median"]);
        const getValueForScaleType = () => {
          if (percentileScaleTypes.has(pivotDefinition.scaleType)) {
            const target =
              pivotDefinition.scaleType === "Median"
                ? 50
                : Number(pivotDefinition.scaleType);
            const found = Array.isArray(percentiles)
              ? percentiles.find((p: any) => p?.percentile === target)
              : undefined;
            return found ? (found[analytics] ?? null) : null;
          }
          return values?.[analytics] ?? null;
        };

        if (add2RowMap) {
          entityRowsMap[entityId][rowField] = getValueForScaleType();
          betaDefinitions.forEach((betaDef) => {
            const { betaId, betaValue, covarianceType } = betaDef;
            const betaSummaryObject = betaSummaries?.find(
              ({ primaryKey }) =>
                primaryKey.betaId === betaId &&
                primaryKey.covarType === covarianceType &&
                primaryKey.entityId === entityId,
            );
            if (betaSummaryObject) {
              const betaValuesObject = betaSummaryObject.values || [];
              let betaRowField = toBetaRowField(bucket, key, betaDef.key);
              if (scenario) {
                betaRowField += " - scenario";
              }
              entityRowsMap[entityId][betaRowField] =
                betaValuesObject[betaValue.toLowerCase()];
            }
          });
          faDefinitions.forEach((faDef) => {
            const { factribFactor, factribPeriod } = faDef;
            const slaSummaryObject = slaSummaries?.find(
              ({ primaryKey }) =>
                primaryKey.period === factribPeriod &&
                primaryKey.entityId === entityId,
            );
            if (slaSummaryObject) {
              const faValuesContribObject =
                slaSummaryObject.valuesContrib || {};
              let faRowField = toFaRowField(bucket, key, faDef.key);
              if (scenario) {
                faRowField += " - scenario";
              }
              entityRowsMap[entityId][faRowField] =
                faValuesContribObject[factribFactor.toLowerCase()];
            }
          });
        }
      };

      groupingNodes.forEach((groupingNode) => {
        const { entitySummaries = [], bucket = "N/A" } = groupingNode;
        entitySummaries.forEach((entitySummary = {}) => {
          processEntitySummary(bucket, entitySummary, false);
        });
      });
      groupingNodesSbs.forEach((groupingNode) => {
        const { entitySummaries = [], bucket = "N/A" } = groupingNode;
        entitySummaries.forEach((entitySummary = {}) => {
          processEntitySummary(bucket, entitySummary, true);
        });
      });
    });
    return entityRowsMap;
  }, [baseQueries, definitions, sbsQueries]);
};

export const useDataQueries = () => {
  const pivotDefinitions = useSelector<any, PivotDefinition[]>(
    selectDefinitions(SelectionState.COMMITTED),
  );
  const queryEnabled: boolean = useQueryEnabled();
  const [baseParams, sbsBaseParams] = useSelector<any, any>(
    selectBaseGroupingParams,
  );
  const [baseQueryOptions, sbsQueryOptions] = useMemo(() => {
    const _baseQueryOptions: QueryObserverOptions[] = [],
      _sbsQueryOptions: QueryObserverOptions[] = [];
    pivotDefinitions?.forEach((pivotDefinition) => {
      const params: any = {
        levels: pivotDefinition.levels,
        analytics: pivotDefinition.analytics,
        summary: pivotDefinition.summary,
        lookthru: pivotDefinition.lookthru,
        results: [...(baseParams?.results ?? [])],
      };
      // Apply percentile and ratio parameters similar to analytics
      const percentileScaleTypes = new Set(["25", "75", "Median"]);
      if (percentileScaleTypes.has(pivotDefinition.scaleType)) {
        params.percentile_analytics = pivotDefinition.analytics;
        params.percentiles =
          pivotDefinition.scaleType === "Median"
            ? "50"
            : pivotDefinition.scaleType;
      }
      if (pivotDefinition.scaleType === "RATIO") {
        // Ensure ratio semantics for relative calls
        params.relativeTypes = "RATIO";
      }
      if (pivotDefinition.workspaceTradeType !== "DEFAULT") {
        params.workspace_trade_types =
          pivotDefinition.workspaceTradeType.toUpperCase();
      }
      if (pivotDefinition.betaDefinitions?.length) {
        params.results.push("BETA_ATTRIBUTES");
        const betaTypesSet = new Set(
          pivotDefinition.betaDefinitions.map((def) => def.betaTypeParameter),
        );
        params.beta_types = Array.from(betaTypesSet).join(",");
        const betaFactorsSet = new Set(
          pivotDefinition.betaDefinitions.map((def) => def.betaFactorParameter),
        );
        params.beta_factors = Array.from(betaFactorsSet).join(",");
      }
      if (pivotDefinition.pruneBuckets) {
        params.results.push("PRUNE_BUCKETS");
      }
      if (pivotDefinition.filterDefinitions?.length) {
        params.bucket_filter_type = PivotFilterDefinition.filterTypeParameter(
          pivotDefinition.filterDefinitions,
        );
        params.bucket_filters = PivotFilterDefinition.filtersParameter(
          pivotDefinition.filterDefinitions,
        );
      }
      if (pivotDefinition.faDefinitions?.length) {
        params.results.push("SECURITY_LEVEL_ATTRIBUTES");
        params.factrib_factors = pivotDefinition.faDefinitions
          .map((d) => d.factribFactor)
          .join(",");
        params.factrib_periods = pivotDefinition.faDefinitions
          .map((d) => d.factribPeriod)
          .join(",");
      }

      const url = new URL(
        resolveIn2ServiceUrl(process.env.REACT_APP_BASE_URL_GROUPING_SERVICE),
      );

      _baseQueryOptions.push({
        enabled: queryEnabled,
        queryKey: ["pivot", baseParams, params],
        queryFn: async () => {
          Object.entries({
            ...baseParams,
            ...params,
          }).forEach(([key, value]) =>
            url.searchParams.append(key, value as string),
          );
          const { data } = await exchange({
            baseURL: url.toString(),
          }).then((response) => response);
          //const response = await window.fetch(url.toString());
          // const json = await response.json();

          return data;
        },
      });

      if (sbsBaseParams) {
        const _url = new URL(
          resolveIn2ServiceUrl(process.env.REACT_APP_BASE_URL_GROUPING_SERVICE),
        );
        _sbsQueryOptions.push({
          queryKey: ["pivot-sbs", sbsBaseParams, params],
          queryFn: async () => {
            Object.entries({
              ...sbsBaseParams,
              ...params,
            }).forEach(([key, value]) =>
              _url.searchParams.append(key, value as string),
            );
            const { data } = await exchange({
              baseURL: _url.toString(),
            }).then((response) => response);
            return data;
          },
        });
      }
    });
    return [_baseQueryOptions, _sbsQueryOptions];
  }, [baseParams, pivotDefinitions, queryEnabled, sbsBaseParams]);
  const _baseQueries = useQueries({ queries: baseQueryOptions });
  const _sbsQueries = useQueries({ queries: sbsQueryOptions });

  //to get around issue https://github.com/tannerlinsley/react-query/issues/3049
  //this should be fixed in react-query v4
  const [baseQueries, setBaseQueries] = useState(_baseQueries);
  const [sbsQueries, setSbsQueries] = useState(_sbsQueries);
  useDeepCompareEffect(() => {
    setBaseQueries(_baseQueries);
  }, [_baseQueries]);
  useDeepCompareEffect(() => {
    setSbsQueries(_sbsQueries);
  }, [_sbsQueries]);

  return [baseQueries, sbsQueries];
};
